import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

// ============================================================================
// INTERFACES DE ANOTAÇÕES
// ============================================================================

export interface LessonNote {
  lessonId: string;
  content: string;
  updatedAt: string;
}

@Injectable({
  providedIn: 'root',
})
export class NotesService {
  // ==========================================================================
  // PROPRIEDADES
  // ==========================================================================

  private readonly STORAGE_KEY_PREFIX = 'course_viewer_notes_';
  private readonly AUTOSAVE_DELAY_MS = 1000; // 1 segundo de debounce

  private currentNoteSubject = new BehaviorSubject<string>('');
  private savingSubject = new BehaviorSubject<boolean>(false);
  private saveErrorSubject = new Subject<string | null>();

  // ==========================================================================
  // OBSERVABLES PÚBLICOS
  // ==========================================================================

  public currentNote$ = this.currentNoteSubject.asObservable();
  public saving$ = this.savingSubject.asObservable();
  public saveError$ = this.saveErrorSubject.asObservable();

  // ==========================================================================
  // CONSTRUCTOR
  // ==========================================================================

  constructor() {
    // Configurar autosave com debounce
    this.setupAutosave();
  }

  // ==========================================================================
  // MÉTODOS CRUD - READ
  // ==========================================================================

  /**
   * Obtém a anotação de uma lição específica
   * @param lessonId ID da lição
   * @returns Conteúdo da anotação ou string vazia
   */
  public getNote(lessonId: string): string {
    try {
      const storageKey = this.getStorageKey(lessonId);
      const stored = localStorage.getItem(storageKey);

      if (stored) {
        const note: LessonNote = JSON.parse(stored);
        return note.content || '';
      }

      return '';
    } catch (error) {
      console.error('Erro ao carregar anotação:', error);
      return '';
    }
  }

  /**
   * Carrega a anotação de uma lição específica
   * @param lessonId ID da lição
   */
  public loadNote(lessonId: string): void {
    // Cancelar qualquer salvamento pendente ao mudar de lição
    this.cancelPendingSave();

    const content = this.getNote(lessonId);
    this.currentNoteSubject.next(content);
    this.saveErrorSubject.next(null);
  }

  // ==========================================================================
  // MÉTODOS CRUD - UPDATE
  // ==========================================================================

  /**
   * Atualiza o conteúdo da anotação atual (dispara autosave)
   * @param content Conteúdo da anotação
   * @param lessonId ID da lição
   */
  public updateNote(content: string, lessonId: string): void {
    this.currentNoteSubject.next(content);
    this.saveNoteDebounced(content, lessonId);
  }

  /**
   * Salva a anotação de uma lição específica
   * @param lessonId ID da lição
   * @param content Conteúdo da anotação
   */
  public saveNote(lessonId: string, content: string): void {
    try {
      this.savingSubject.next(true);
      this.saveErrorSubject.next(null);

      const note: LessonNote = {
        lessonId,
        content: content.trim(),
        updatedAt: new Date().toISOString(),
      };

      const storageKey = this.getStorageKey(lessonId);
      localStorage.setItem(storageKey, JSON.stringify(note));

      // Simular pequeno delay para feedback visual
      setTimeout(() => {
        this.savingSubject.next(false);
      }, 300);
    } catch (error) {
      console.error('Erro ao salvar anotação:', error);
      this.savingSubject.next(false);
      this.saveErrorSubject.next('Erro ao salvar anotação. Tente novamente.');
    }
  }

  /**
   * Exclui a anotação de uma lição específica
   * @param lessonId ID da lição
   */
  public deleteNote(lessonId: string): void {
    try {
      const storageKey = this.getStorageKey(lessonId);
      localStorage.removeItem(storageKey);
      this.currentNoteSubject.next('');
      this.saveErrorSubject.next(null);
    } catch (error) {
      console.error('Erro ao excluir anotação:', error);
      this.saveErrorSubject.next('Erro ao excluir anotação.');
    }
  }

  // ==========================================================================
  // MÉTODOS AUXILIARES
  // ==========================================================================

  /**
   * Limpa o estado atual das anotações
   */
  public clearCurrentNote(): void {
    this.currentNoteSubject.next('');
    this.saveErrorSubject.next(null);
  }

  /**
   * Obtém todas as anotações salvas
   * @returns Mapa de lessonId para LessonNote
   */
  public getAllNotes(): Map<string, LessonNote> {
    const notes = new Map<string, LessonNote>();

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.STORAGE_KEY_PREFIX)) {
          const stored = localStorage.getItem(key);
          if (stored) {
            const note: LessonNote = JSON.parse(stored);
            notes.set(note.lessonId, note);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar todas as anotações:', error);
    }

    return notes;
  }

  // ==========================================================================
  // MÉTODOS PRIVADOS
  // ==========================================================================

  /**
   * Obtém a chave de armazenamento para uma lição
   * @param lessonId ID da lição
   * @returns Chave de armazenamento
   */
  private getStorageKey(lessonId: string): string {
    return `${this.STORAGE_KEY_PREFIX}${lessonId}`;
  }

  /**
   * Configura o autosave com debounce
   */
  private setupAutosave(): void {
    // Este método será usado quando implementarmos autosave reativo
    // Por enquanto, o autosave é feito diretamente no updateNote
  }

  /**
   * Salva a anotação com debounce
   * @param content Conteúdo da anotação
   * @param lessonId ID da lição
   */
  private saveNoteDebounced(content: string, lessonId: string): void {
    // Cancelar salvamento anterior se existir
    this.cancelPendingSave();

    // Agendar novo salvamento
    this.debounceTimer = setTimeout(() => {
      this.saveNote(lessonId, content);
      this.debounceTimer = null;
    }, this.AUTOSAVE_DELAY_MS);
  }

  /**
   * Cancela qualquer salvamento pendente
   */
  private cancelPendingSave(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
}
